'use client';

import { useEffect } from 'react';
import { DEFAULT_SETTLE_DELAY_MS, READINESS_TIMEOUT_MS } from '../../core/constants';

type TrackingWindow = Window & {
  _satellite?: {
    track?: (dcr: string) => void;
  } | null;
};

type ScriptWithReadyState = HTMLScriptElement & {
  readyState?: string;
};

function getTrackingWindow(): TrackingWindow {
  return window as unknown as TrackingWindow;
}

function isLaunchTrackReady(): boolean {
  return typeof getTrackingWindow()._satellite?.track === 'function';
}

function isScriptAlreadyLoaded(script: HTMLScriptElement): boolean {
  const readyState = (script as ScriptWithReadyState).readyState;
  return readyState === 'complete' || readyState === 'loaded';
}

/** Loads the Adobe Launch seed once and reports when its script is ready. */
export function useTrackingSeed({
  src,
  onLoad,
}: {
  src: string | undefined;
  onLoad: () => void;
}): void {
  useEffect(() => {
    if (!src || typeof window === 'undefined') {
      return;
    }

    if (isLaunchTrackReady()) {
      onLoad();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-name="launch"]');
    const script = existingScript ?? document.createElement('script');
    let pollId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const stopPolling = () => {
      if (pollId !== null) {
        clearInterval(pollId);
        pollId = null;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const notifyWhenTrackReady = () => {
      if (cancelled) {
        return;
      }
      if (isLaunchTrackReady()) {
        stopPolling();
        onLoad();
        return;
      }

      if (pollId !== null) {
        return;
      }

      pollId = setInterval(() => {
        if (cancelled) {
          stopPolling();
          return;
        }
        if (isLaunchTrackReady()) {
          stopPolling();
          onLoad();
        }
      }, DEFAULT_SETTLE_DELAY_MS);
      timeoutId = setTimeout(() => {
        stopPolling();
      }, READINESS_TIMEOUT_MS);
    };

    const handleLoad = () => {
      notifyWhenTrackReady();
    };

    script.addEventListener('load', handleLoad);

    if (!existingScript) {
      script.src = src;
      script.async = true;
      script.dataset['name'] = 'launch';
      document.head.appendChild(script);
    } else if (isScriptAlreadyLoaded(script)) {
      notifyWhenTrackReady();
    }

    return () => {
      cancelled = true;
      stopPolling();
      script.removeEventListener('load', handleLoad);
    };
  }, [onLoad, src]);
}
